/**
 * Job-based role bundles for the Users & roles screen (ADR-0065, on top of ADR-0053): an
 * administrator picks what the person *does* ("Case worker", "Approver", "Agent builder",
 * "Team admin" …) and the console ticks the realm roles that job needs. The roles stay visible
 * and editable; the identity service and OPA decide as before.
 */

export interface RolePreset {
  /** i18n key `pickers:preset.<key>.name` / `.can`. */
  key: string;
  roles: readonly string[];
}

export const ROLE_PRESETS: readonly RolePreset[] = [
  { key: "case_worker", roles: ["case-worker"] },
  { key: "case_supervisor", roles: ["case-worker", "case-supervisor"] },
  { key: "approver", roles: ["approver", "reviewer"] },
  { key: "finance_approver", roles: ["approver", "finance-approver"] },
  { key: "agent_builder", roles: ["builder", "retrieval-curator", "eval-curator"] },
  { key: "release_approver", roles: ["release-approver"] },
  { key: "contact_centre_agent", roles: ["channel-agent"] },
  { key: "contact_centre_supervisor", roles: ["channel-agent", "channel-supervisor"] },
  { key: "privacy_officer", roles: ["privacy-officer"] },
  { key: "auditor", roles: ["auditor"] },
  { key: "manager", roles: ["executive"] },
  { key: "knowledge_curator", roles: ["retrieval-curator"] },
  { key: "team_admin", roles: ["tenant-admin", "case-admin", "agent-admin", "channel-admin", "guardrails-admin"] },
  // Platform team only: the identity service grants these roles to platform-tenant users only,
  // so the screen offers them only there.
  { key: "platform_admin", roles: ["platform-admin", "tool-admin", "model-admin", "marketplace-curator"] },
  { key: "platform_auditor", roles: ["platform-auditor"] },
];

/** A user's roles as bundles: the bundles they hold and the roles no held bundle covers. */
export function rolesAsBundles(roles: readonly string[], presets: readonly RolePreset[] = ROLE_PRESETS): { bundles: string[]; single: string[] } {
  const bundles = presetsHeld(roles, presets);
  const covered = new Set(presets.filter((p) => bundles.includes(p.key)).flatMap((p) => p.roles));
  return { bundles, single: roles.filter((r) => !covered.has(r)) };
}

/** Presets whose roles are all present (and assignable). */
export function presetsHeld(roles: readonly string[], presets: readonly RolePreset[] = ROLE_PRESETS): string[] {
  const set = new Set(roles);
  return presets.filter((p) => p.roles.every((r) => set.has(r))).map((p) => p.key);
}

/** Adds a preset's roles (only those the administrator may grant). */
export function addPreset(roles: readonly string[], preset: RolePreset, assignable: (role: string) => boolean): string[] {
  return [...new Set([...roles, ...preset.roles.filter(assignable)])];
}

/** Removes a preset's roles, keeping roles another held preset still needs. */
export function removePreset(roles: readonly string[], preset: RolePreset, presets: readonly RolePreset[] = ROLE_PRESETS): string[] {
  const others = presets.filter((p) => p.key !== preset.key && p.roles.every((r) => roles.includes(r)));
  const keep = new Set(others.flatMap((p) => p.roles));
  return roles.filter((r) => !preset.roles.includes(r) || keep.has(r));
}
